<template>
	<div v-if="showGuide && currentSteps" class="tutorial-container" :style="containerStyle">
		<div class="tutorial-background" :style="currentBackgroundStyle">
			<div class="tutorial-dialog" :style="dialogStyle" ref="dialog">
				<!-- 新增引导线容器 -->
				<div
					v-if="currentStep.guideLineDirection"
					class="guide-line-container"
					:style="{
						'--line-length': currentLineOptions.computedLength + 'px',
						'--offset-x': currentLineOptions.computedOffset.x,
						'--offset-y': currentLineOptions.computedOffset.y
					}"
				>
					<div class="guide-line" :class="`guide-line-${currentStep.guideLineDirection}`"></div>
				</div>
				<div class="tutorial-content" :style="contentStyle">
					<div class="tutorial-text" v-html="currentStep.content"></div>

					<div class="tutorial-buttons">
						<button v-if="showSkip && !showAction" @click="skip" class="tutorial-button skip">
							{{ skipText }}
							<span v-if="shouldShowCounter" class="step-counter">
								({{ currentStepIndex + 1 }}/{{ totalSteps }})
							</span>
						</button>
						<button v-if="showPrev" @click="prev" class="tutorial-button prev">
							{{ prevText }}
						</button>
						<button v-if="showNext" @click="next" class="tutorial-button next">
							{{ nextText }}
						</button>
						<button v-if="showAction" @click="action" class="tutorial-button action">
							{{ actionText }}
						</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { addUserRecord } from '@/api/guide'
const props = defineProps({
	guideData: {
		type: Object,
		required: true,
		validator: data => {
			return Object.values(data).every(
				page =>
					Array.isArray(page?.steps) &&
					page.steps.every(
						step =>
							step.content &&
							typeof step.content === 'string' &&
							typeof step.x === 'number' &&
							typeof step.y === 'number' &&
							step.backgroundImage
					)
			)
		}
	},

	skipText: {
		type: String,
		default: '跳过'
	},
	prevText: {
		type: String,
		default: '上一步'
	},
	nextText: {
		type: String,
		default: '下一步'
	},
	actionText: {
		type: String,
		default: '去创作'
	},
	baseWidth: {
		type: Number,
		default: 1920
	},
	baseHeight: {
		type: Number,
		default: 1080
	},
	baseDialogWidth: {
		type: Number,
		default: 496 // 1920分辨率下的弹框宽度
	},
	baseDialogHeight: {
		type: Number,
		default: 220 // 1920分辨率下的弹框高度
	}
})
// console.log(props.guideData,'=========datatatatatat')

const emit = defineEmits(['skip', 'action', 'step-change'])
const route = useRoute()
const currentSteps = computed(() => {
	return props.guideData[route.path]?.steps || []
})
// 获取id
const routeid = computed(() => props.guideData[route.path]?.id || '')
// 新增计算属性
const totalSteps = computed(() => currentSteps.value.length)
const shouldShowCounter = computed(() => {
	const isNotFinal = !currentStep.value.isFinal
	const hasCounter = currentStep.value.stepNum !== false // 默认显示，除非显式设为false
	return showSkip.value && isNotFinal && hasCounter
})
const showGuide = computed(() => currentSteps.value.length > 0)
const currentStepIndex = ref(0)
const dialog = ref(null)
const windowSize = ref({
	width: window.innerWidth,
	height: window.innerHeight
})
const guideLineContainerStyle = computed(() => ({
	'--guide-offset-x': currentLineOptions.value.offset?.x || '0px',
	'--guide-offset-y': currentLineOptions.value.offset?.y || '0px'
}))
// 计算缩放比例
const scaleRatio = computed(() => {
	const baseRatio = props.baseWidth / props.baseHeight
	const windowRatio = windowSize.value.width / windowSize.value.height

	if (windowRatio > baseRatio) {
		// 窗口更宽，高度填满，按高度比例缩放
		return windowSize.value.height / props.baseHeight
	} else {
		// 窗口更高，宽度填满，按宽度比例缩放
		return windowSize.value.width / props.baseWidth
	}
})

// 监听窗口变化
const handleResize = () => {
	windowSize.value = {
		width: window.innerWidth,
		height: window.innerHeight
	}
}

onMounted(() => {
	window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
	window.removeEventListener('resize', handleResize)
})

// 计算当前步骤
const currentStep = computed(() => currentSteps.value[currentStepIndex.value])
const guideLineOptions = computed(() => currentStep.value.guideLineOptions)
// 计算容器样式
const containerStyle = computed(() => ({
	position: 'fixed',
	top: 0,
	left: 0,
	width: '100%',
	height: '100%',
	zIndex: 9999,
	backgroundColor: 'black',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center'
}))

// 计算当前背景样式
const currentBackgroundStyle = computed(() => {
	const ratio = props.baseWidth / props.baseHeight
	const windowRatio = windowSize.value.width / windowSize.value.height

	let width, height

	if (windowRatio > ratio) {
		// 窗口更宽，高度填满，宽度按比例
		height = '100%'
		width = `${windowSize.value.height * ratio}px`
	} else {
		// 窗口更高，宽度填满，高度按比例
		width = '100%'
		height = `${windowSize.value.width / ratio}px`
	}

	return {
		position: 'relative',
		width,
		height,
		backgroundImage: `url(${currentStep.value.backgroundImage})`,
		backgroundSize: 'cover',
		backgroundPosition: 'center',
		backgroundRepeat: 'no-repeat',
		transition: 'background-image 0.3s ease-in-out'
	}
})

// 计算弹框内容样式（字体大小等按比例缩放）
const contentStyle = computed(() => ({
	fontSize: `${14 * scaleRatio.value}px`,
	lineHeight: `${1.5 * scaleRatio.value}`
}))
// 更新计算属性，包含偏移量默认值
// const currentLineOptions = computed(() => ({
// 	color: '#1890ff',
// 	width: '2px',
// 	length: '40px',
// 	dotSize: '10px',
// 	dotBorder: '2px',
// 	dotBorderColor: 'rgba(255,255,255,0.5)',
// 	offset: { x: '0', y: '0' }, // 默认无偏移
// 	...(currentStep.value.guideLineOptions || {})
// }))
const currentLineOptions = computed(() => {
	const baseOptions = {
		color: '#1890ff',
		width: '2px',
		length: 40,
		dotSize: '10px',
		dotBorder: '2px',
		dotBorderColor: 'rgba(255,255,255,0.5)',
		offset: {
			x: `${currentStep.value.guideLineOptions?.offset?.x || 0 * scaleRatio.value}px`,
			y: `${currentStep.value.guideLineOptions?.offset?.y || 0 * scaleRatio.value}px`
		} // 默认无偏移
		// 其他默认配置...
	}

	const customOptions = currentStep.value.guideLineOptions || {}

	return {
		...baseOptions,
		...customOptions,
		// 动态计算实际长度和偏移
		computedLength: (customOptions.length || baseOptions.length) * scaleRatio.value,
		computedOffset: {
			x: (customOptions.offset?.x || 0) * scaleRatio.value + 'px',
			y: (customOptions.offset?.y || 0) * scaleRatio.value + 'px'
		}
	}
})
// 修改后的对话框位置计算
const dialogStyle = computed(() => {
	if (!currentStep.value) return {}

	// 计算背景图的实际显示尺寸
	const baseRatio = props.baseWidth / props.baseHeight
	let bgDisplayWidth, bgDisplayHeight

	if (windowSize.value.width / windowSize.value.height > baseRatio) {
		// 背景图高度填满窗口
		bgDisplayHeight = windowSize.value.height
		bgDisplayWidth = bgDisplayHeight * baseRatio
	} else {
		// 背景图宽度填满窗口
		bgDisplayWidth = windowSize.value.width
		bgDisplayHeight = bgDisplayWidth / baseRatio
	}

	// 计算位置比例 (基于1920*1080设计图的百分比位置)
	const leftRatio = currentStep.value.x / props.baseWidth
	const topRatio = currentStep.value.y / props.baseHeight

	// 应用到实际显示的背景图上
	const left = leftRatio * bgDisplayWidth
	const top = topRatio * bgDisplayHeight

	return {
		position: 'absolute',
		left: `${left}px`,
		top: `${top}px`,
		width: `${props.baseDialogWidth * scaleRatio.value}px`,
		minHeight: `${props.baseDialogHeight * scaleRatio.value}px`,
		transform: 'translate(0, 0)',
		transition:
			'left 0.3s ease-in-out, top 0.3s ease-in-out, width 0.3s ease-in-out, height 0.3s ease-in-out'
	}
})

// 按钮显示逻辑
const showSkip = computed(() => true)
const showPrev = computed(() => currentStepIndex.value > 0)
const showNext = computed(
	() => currentStepIndex.value < currentSteps.value.length - 1 && !currentStep.value.isFinal
)
const showAction = computed(
	() => currentStep.value.isFinal || currentStepIndex.value === currentSteps.value.length - 1
)

// 导航方法
const next = () => {
	if (currentStepIndex.value < currentSteps.value.length - 1) {
		currentStepIndex.value++
		emit('step-change', currentStepIndex.value)
	}
}

const prev = () => {
	if (currentStepIndex.value > 0) {
		currentStepIndex.value--
		emit('step-change', currentStepIndex.value)
	}
}

const skip = async () => {
	// console.log(routeid.value,'================routeidrouteidrouteid')
	let params = {
		beginnerGuidanceId: routeid.value,
		isChecked: 1
	}
	const res = await addUserRecord(params)
	if (res?.success) {
		emit('skip')
	}
	// console.log(res,'================resresresresresresres')
}

const action = async () => {
	let params = {
		beginnerGuidanceId: routeid.value,
		isChecked: 1
	}
	const res = await addUserRecord(params)
	if (res?.success) {
		emit('action')
	}
}
</script>

<style scoped lang="less">
.tutorial-container {
	background-color: rgba(0, 0, 0, 0.8);
}
.tutorial-background {
	transition: background-image 0.3s ease-in-out;
	will-change: background-image; /* 启用GPU加速 */
}
.tutorial-dialog {
	position: absolute;
	background: linear-gradient(45deg, #181613 20%, #0f0c08 80% /* 改用更深的颜色 */),
		radial-gradient(100% 100% at 100% 0%, rgba(255, 82, 25, 0.4) 0%, rgba(255, 82, 25, 0) 70%);
	border-radius: calc(8px * v-bind('scaleRatio'));
	padding: calc(20px * v-bind('scaleRatio'));
	box-shadow: 0 calc(0px * v-bind('scaleRatio')) calc(10px * v-bind('scaleRatio'))
		rgba(0, 0, 0, 0.15);
	max-width: calc(400px * v-bind('scaleRatio'));
	min-width: calc(300px * v-bind('scaleRatio'));
	display: flex;
	flex-direction: column;
}

.tutorial-content {
	// background: pink;
	height: 100%;
	display: flex;
	flex-direction: column;
	flex: 1;
	justify-content: space-between;

	/* gap: calc(20px * v-bind('scaleRatio'));  */
}

.tutorial-text {
	line-height: 1.5;
	color: #333;
	// background: #000;
}
.tutorial-text :deep(.title) {
	font-size: calc(18px * v-bind('scaleRatio')) !important;
	line-height: calc(28px * v-bind('scaleRatio')) !important;
	margin-bottom: calc(16px * v-bind('scaleRatio')) !important;
	color: #ffffff;
}

.tutorial-text :deep(.content) {
	font-size: calc(16px * v-bind('scaleRatio')) !important;
	color: #ccc8c2;
	:deep(img) {
		width: auto !important; /* 覆盖内联样式 */
		max-width: 100% !important;
	}
}

.tutorial-buttons {
	display: flex;
	justify-content: flex-end;
	gap: calc(10px * v-bind('scaleRatio'));
	flex-wrap: wrap;
	margin-top: 8px;
}

.tutorial-button {
	padding: calc(8px * v-bind('scaleRatio')) calc(16px * v-bind('scaleRatio'));
	border-radius: calc(4px * v-bind('scaleRatio'));
	border: none;
	cursor: pointer;
	transition: all 0.2s;
}

.tutorial-button.skip {
	background-color: transparent;
	color: #999691;
	// 左侧对齐
	margin-right: auto;
}

.tutorial-button.prev {
	background-color: #e0e0e0;
	color: #333;
	background: rgba(255, 213, 144, 0.1);
	border: 1px solid rgba(251, 211, 141, 0.2);
	color: #fbd38d;
	font-family: 'MiSans-Medium';
}

.tutorial-button.next {
	background: #fbd38d;
	color: #141413;
	font-family: 'MiSans-Medium';
}

.tutorial-button.action {
	background: #fbd38d;
	color: #141413;
	font-family: 'MiSans-Medium';
}

.tutorial-button:hover {
	opacity: 0.9;
	transform: translateY(calc(-1px * v-bind('scaleRatio')));
}
/* 引导线基础样式 */
// .guide-line::after {
// 	content: '';
// 	position: absolute;
// 	background-color: v-bind('guideLineOptions.color');
// 	width: v-bind('guideLineOptions.width');
// 	z-index: 1;
// }

// /* 圆点样式 */
// .guide-line::before {
// 	content: '';
// 	position: absolute;
// 	width: v-bind('guideLineOptions.dotSize');
// 	height: v-bind('guideLineOptions.dotSize');
// 	border-radius: 50%;
// 	background-color: v-bind('guideLineOptions.color');
// 	border: v-bind('guideLineOptions.dotBorder') solid v-bind('guideLineOptions.dotBorderColor');
// 	z-index: 1;
// }
.guide-line::before {
	content: '';
	position: absolute;
	width: calc(v-bind('currentLineOptions.dotSize') + 2 * v-bind('currentLineOptions.dotBorder'));
	height: calc(v-bind('currentLineOptions.dotSize') + 2 * v-bind('currentLineOptions.dotBorder'));
	border-radius: 50%;
	background-color: v-bind('currentLineOptions.dotBorderColor');
	box-sizing: border-box;

	/* 中心圆点 */
	&::after {
		content: '';
		position: absolute;
		width: v-bind('currentLineOptions.dotSize');
		height: v-bind('currentLineOptions.dotSize');
		background-color: v-bind('currentLineOptions.dotBackground');
		border-radius: 50%;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
	}
}

/* 不同方向的引导线 */
.guide-line-top::after {
	bottom: 100%;
	left: 50%;
	height: v-bind('guideLineOptions.length');
	transform: translateX(-50%);
}

.guide-line-top::before {
	bottom: calc(100% + v-bind('guideLineOptions.length'));
	left: 50%;
	transform: translateX(-50%);
}

.guide-line-right::after {
	left: 100%;
	top: 50%;
	height: v-bind('guideLineOptions.width');
	width: v-bind('guideLineOptions.length');
	transform: translateY(-50%);
}

.guide-line-right::before {
	left: calc(100% + v-bind('guideLineOptions.length'));
	top: 50%;
	transform: translateY(-50%);
}

.guide-line-bottom::after {
	top: 100%;
	left: 50%;
	height: v-bind('guideLineOptions.length');
	transform: translateX(-50%);
}

.guide-line-bottom::before {
	top: calc(100% + v-bind('guideLineOptions.length'));
	left: 50%;
	transform: translateX(-50%);
}

.guide-line-left::after {
	right: 100%;
	top: 50%;
	height: v-bind('guideLineOptions.width');
	width: v-bind('guideLineOptions.length');
	transform: translateY(-50%);
}

.guide-line-left::before {
	right: calc(100% + v-bind('guideLineOptions.length'));
	top: 50%;
	transform: translateY(-50%);
}
/* 引导线容器 */
// .guide-line-container {
// 	position: absolute;
// 	pointer-events: none;
// }

/* 引导线基础样式 */
.guide-line {
	position: relative;
}

.guide-line::after,
.guide-line::before {
	content: '';
	position: absolute;
	pointer-events: none;
}
/* 引导线 */
.guide-line::after {
	background-color: v-bind('currentLineOptions.color');
	z-index: 1;
	width: var(--line-length, 40px);
}

/* 圆点 */
.guide-line::before {
	width: v-bind('currentLineOptions.dotSize');
	height: v-bind('currentLineOptions.dotSize');
	border-radius: 50%;
	background-color: v-bind('currentLineOptions.color');
	border: v-bind('currentLineOptions.dotBorder') solid v-bind('currentLineOptions.dotBorderColor');
	z-index: 1;
}

/* 引导线容器 - 控制整体偏移 */
.guide-line-container {
	position: absolute;
	pointer-events: none;
	// transform: translate(var(--guide-offset-x), var(--guide-offset-y));
	//  transform: translateY(-50%) translateX(var(--offset-x)) translateY(var(--offset-y)) !important;
	transform: 
    /* 方向定位（示例：右侧） */ translateY(-50%) /* 动态偏移 */
		translateX(var(--offset-x, 0)) translateY(var(--offset-y, 0)) !important; /* 确保优先级 */
}

/* 右侧引导线 */
.guide-line-right .guide-line-container {
	left: 100%;
	top: 50%;
	// transform: translate(0, -50%) translate(var(--guide-offset-x), var(--guide-offset-y));
	transform: translateY(-50%) translateX(var(--offset-x, 0)) translateY(var(--offset-y, 0));
}

.guide-line-right::after {
	left: 0;
	top: 50%;
	height: v-bind('currentLineOptions.width');
	// width: v-bind('currentLineOptions.length');
	transform: translateY(-50%);
	// background-color: v-bind('currentLineOptions.color');
}

.guide-line-right::before {
	// left: v-bind('currentLineOptions.length');
	// top: 50%;
	// transform: translateY(-50%);
	// width: v-bind('currentLineOptions.dotSize');
	// height: v-bind('currentLineOptions.dotSize');
	// border: v-bind('currentLineOptions.dotBorder') solid v-bind('currentLineOptions.dotBorderColor');
	left: var(--line-length, 40px);
	top: 50%;
	transform: translateY(-50%);
}

/* 左侧引导线 */
.guide-line-left .guide-line-container {
	right: 100%;
	top: 50%;
	// transform: translate(0, -50%) translate(calc(-1 * var(--guide-offset-x)), var(--guide-offset-y));
	transform: translateY(-50%) translateX(calc(-1 * var(--offset-x, 0)))
		translateY(var(--offset-y, 0));
}

.guide-line-left::after {
	right: 0;
	top: 50%;
	// height: v-bind('currentLineOptions.width');
	// width: v-bind('currentLineOptions.length');
	// transform: translateY(-50%);
	// background-color: v-bind('currentLineOptions.color');
	height: v-bind('currentLineOptions.width');
	transform: translateY(-50%);
}

.guide-line-left::before {
	right: var(--line-length, 40px);
	top: 50%;
	transform: translateY(-50%);
	// right: v-bind('currentLineOptions.length');
	// top: 50%;
	// transform: translateY(-50%);
	// width: v-bind('currentLineOptions.dotSize');
	// height: v-bind('currentLineOptions.dotSize');
	// border: v-bind('currentLineOptions.dotBorder') solid v-bind('currentLineOptions.dotBorderColor');
}

/* 上方引导线 */
.guide-line-top .guide-line-container {
	bottom: 100%;
	left: 50%;
	// transform: translate(-50%, 0) translate(var(--guide-offset-x), calc(-1 * var(--guide-offset-y)));
	transform: translateX(-50%) translateX(var(--offset-x, 0))
		translateY(calc(-1 * var(--offset-y, 0)));
}

.guide-line-top::after {
	bottom: 0;
	left: 50%;
	// width: v-bind('currentLineOptions.width');
	// height: v-bind('currentLineOptions.length');
	// transform: translateX(-50%);
	// background-color: v-bind('currentLineOptions.color');
	width: v-bind('currentLineOptions.width');
	height: var(--line-length, 40px);
	transform: translateX(-50%);
}

.guide-line-top::before {
	bottom: var(--line-length, 40px);
	left: 50%;
	transform: translateX(-50%);
	// bottom: v-bind('currentLineOptions.length');
	// left: 50%;
	// transform: translateX(-50%);
	// width: v-bind('currentLineOptions.dotSize');
	// height: v-bind('currentLineOptions.dotSize');
	// border: v-bind('currentLineOptions.dotBorder') solid v-bind('currentLineOptions.dotBorderColor');
}

/* 下方引导线 */
.guide-line-bottom .guide-line-container {
	top: 100%;
	left: 50%;
	transform: translateX(-50%) translateX(var(--offset-x, 0)) translateY(var(--offset-y, 0));
	// transform: translate(-50%, 0) translate(var(--guide-offset-x), var(--guide-offset-y));
}

.guide-line-bottom::after {
	top: 0;
	left: 50%;
	// width: v-bind('currentLineOptions.width');
	// height: v-bind('currentLineOptions.length');
	// transform: translateX(-50%);
	// background-color: v-bind('currentLineOptions.color');
	width: v-bind('currentLineOptions.width');
	height: var(--line-length, 40px);
	transform: translateX(-50%);
}

.guide-line-bottom::before {
	top: var(--line-length, 40px);
	left: 50%;
	transform: translateX(-50%);
	// top: v-bind('currentLineOptions.length');
	// left: 50%;
	// transform: translateX(-50%);
	// width: v-bind('currentLineOptions.dotSize');
	// height: v-bind('currentLineOptions.dotSize');
	// border: v-bind('currentLineOptions.dotBorder') solid v-bind('currentLineOptions.dotBorderColor');
}
</style>
